import * as i1$1 from '@angular/cdk/overlay';
import { Overlay, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import * as i4 from '@angular/cdk/portal';
import { BasePortalOutlet, CdkPortalOutlet, PortalInjector, ComponentPortal, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewEncapsulation, Optional, Inject, ViewChild, HostBinding, HostListener, InjectionToken, TemplateRef, Injectable, SkipSelf, Directive, Input, NgModule } from '@angular/core';
import { Subject, fromEvent, defer, of } from 'rxjs';
import { Directionality } from '@angular/cdk/bidi';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { state, style, transition, animate, trigger } from '@angular/animations';
import * as i1 from '@angular/cdk/a11y';
import * as i5 from '@paysafe-ui/components/icon';
import { PsIconModule } from '@paysafe-ui/components/icon';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { filter, take, takeUntil, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { PsCommonModule } from '@paysafe-ui/components/core';

const DIALOG_BACKDROP_CSS_CLASS = 'ps-dialog-backdrop';
/**
 * Configuration for opening a modal dialog with the PsDialog service.
 */
class PsDialogConfig {
    constructor() {
        /** The ARIA role of the dialog element. */
        this.role = 'dialog';
        /** Custom class for the overlay pane. */
        this.panelClass = '';
        /** Whether the dialog has a backdrop. */
        this.hasBackdrop = true;
        /** Custom class for the backdrop, */
        this.backdropClass = DIALOG_BACKDROP_CSS_CLASS;
        /** Whether the user can use escape or clicking on the backdrop to close the modal. */
        this.disableClose = false;
        /** Data being injected into the child component. */
        this.data = null;
        /** ID of the element that describes the dialog. */
        this.ariaDescribedBy = null;
        /** ID of the element that labels the dialog. */
        this.ariaLabelledBy = null;
        /** Aria label to assign to the dialog element */
        this.ariaLabel = null;
        /** Whether the dialog should focus the first focusable element on open. */
        this.autoFocus = true;
        /**
         * Whether the dialog should restore focus to the
         * previously-focused element, after it's closed.
         */
        this.restoreFocus = true;
        /**
         * Whether the dialog should close when the user goes backwards/forwards in history.
         * Note that this usually doesn't include clicking on links (unless the user is using
         * the `HashLocationStrategy`).
         */
        this.closeOnNavigation = true;
        /**
         * Whether the built-in close button of the dialog should be visible or not.
         */
        this.showCloseButton = true;
        // TODO(): add configuration for lifecycle hooks, ARIA labelling.
    }
}

const animationBody = [
    state('void, exit', style({ opacity: 0 })),
    state('enter', style({ opacity: 1 })),
    transition('* => enter', animate('300ms ease-out', style({ opacity: 1 }))),
    transition('* => void, * => exit', animate('300ms ease-in', style({ opacity: 0 })))
];
/**
 * Animations used by PsDialog.
 * @docs-private
 */
const psDialogAnimations = {
    /** Animation that is applied on the dialog container by defalt. */
    dialogContainer: trigger('dialogContainer', animationBody),
    /** @deprecated @breaking-change 8.0.0 Use `psDialogAnimations.dialogContainer` instead. */
    slideDialog: trigger('slideDialog', animationBody)
};

/**
 * Throws an exception for the case when a ComponentPortal is
 * attached to a DomPortalOutlet without an origin.
 * @docs-private
 */
const throwPsDialogContentAlreadyAttachedError = () => {
    throw Error('Attempting to attach dialog content after content is already attached');
};
/**
 * Internal component that wraps user-provided dialog content.
 * @docs-private
 */
class PsDialogContainerComponent extends BasePortalOutlet {
    get psDialogContaier() {
        return true;
    }
    /** Whether the dialog is a modal one. */
    get ariaModal() {
        return true;
    }
    /** The ARIA role of the dialog element. */
    get role() {
        return this._config.role;
    }
    get ariaLabelledByAttr() {
        return this._config.ariaLabel ? null : this._ariaLabelledBy;
    }
    /** Label of the element that should be considered as the dialog's label. */
    get ariaLabel() {
        return this._config.ariaLabel;
    }
    /** Specifies which element the dialog is described by. */
    get ariaDescribedBy() {
        return this._config.ariaDescribedBy ? this._config.ariaDescribedBy : null;
    }
    get hasCloseButton() {
        return this._config.showCloseButton;
    }
    get titleElement() {
        return this._elementRef.nativeElement.querySelector('.ps-dialog-title');
    }
    get contentElement() {
        return this._elementRef.nativeElement.querySelector('.ps-dialog-content');
    }
    get actionsElement() {
        return this._elementRef.nativeElement.querySelector('.ps-dialog-actions');
    }
    get closeIconElement() {
        return this._elementRef.nativeElement.querySelector('.ps-dialog-close-icon');
    }
    get nativeElement() {
        return this._elementRef.nativeElement;
    }
    constructor(_elementRef, _focusTrapFactory, _changeDetectorRef, _document, 
    /** The dialog configuration. */
    _config) {
        super();
        this._elementRef = _elementRef;
        this._focusTrapFactory = _focusTrapFactory;
        this._changeDetectorRef = _changeDetectorRef;
        this._document = _document;
        this._config = _config;
        /** Element that was focused before the dialog was opened. Save this to restore upon close. */
        this._elementFocusedBeforeDialogWasOpened = null;
        /** State of the dialog animation. */
        this._state = 'enter';
        /** Emits when an animation state changes. */
        this._animationStateChanged = new EventEmitter();
        /** Emits when the close icon has been clicked. */
        this._closeIconClicked = new EventEmitter();
        /** Tab index attribute of the dialog container. */
        this.tabIndex = -1;
        this._ariaLabelledBy = _config.ariaLabelledBy ? _config.ariaLabelledBy : null;
    }
    /**
     * Attach a ComponentPortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throwPsDialogContentAlreadyAttachedError();
        }
        this._savePreviouslyFocusedElement();
        return this._portalOutlet.attachComponentPortal(portal);
    }
    /**
     * Attach a TemplatePortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throwPsDialogContentAlreadyAttachedError();
        }
        this._savePreviouslyFocusedElement();
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    /** Moves the focus inside the focus trap. */
    _trapFocus() {
        const element = this._elementRef.nativeElement;
        if (!this._focusTrap) {
            this._focusTrap = this._focusTrapFactory.create(element);
        }
        // If we were to attempt to focus immediately, then the content of the dialog would not yet be
        // ready in instances where change detection has to run first. To deal with this, we simply
        // wait for the microtask queue to be empty.
        if (this._config.autoFocus) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._focusTrap.focusInitialElementWhenReady();
        }
        else {
            const { activeElement } = this._document;
            // Otherwise ensure that focus is on the dialog container. It's possible that a different
            // component tried to move focus while the open animation was running. See:
            // https://github.com/angular/components/issues/16215. Note that we only want to do this
            // if the focus isn't inside the dialog already, because it's possible that the consumer
            // turned off `autoFocus` in order to move focus themselves.
            if (activeElement !== element && !element.contains(activeElement)) {
                element.focus();
            }
        }
    }
    /** Restores focus to the element that was focused before the dialog opened. */
    _restoreFocus() {
        const toFocus = this._elementFocusedBeforeDialogWasOpened;
        // We need the extra check, because IE can set the `activeElement` to null in some cases.
        // eslint-disable-next-line @typescript-eslint/unbound-method
        if (this._config.restoreFocus && toFocus && typeof toFocus.focus === 'function') {
            toFocus.focus();
        }
        if (this._focusTrap) {
            this._focusTrap.destroy();
        }
    }
    /** Saves a reference to the element that was focused before the dialog was opened. */
    _savePreviouslyFocusedElement() {
        if (this._document) {
            this._elementFocusedBeforeDialogWasOpened = this._document.activeElement;
            // Note that there is no focus method when rendering on the server.
            if (this._elementRef.nativeElement.focus) {
                // Move focus onto the dialog immediately in order to prevent the user from accidentally
                // opening multiple dialogs at the same time. Needs to be async, because the element
                // may not be focusable immediately.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                Promise.resolve().then(() => this._elementRef.nativeElement.focus());
            }
        }
    }
    /** Callback, invoked whenever an animation on the host completes. */
    _onAnimationDone(event) {
        if (event.toState === 'enter') {
            this._trapFocus();
        }
        else if (event.toState === 'exit') {
            this._restoreFocus();
        }
        this._animationStateChanged.emit(event);
    }
    /** Callback, invoked when an animation on the host starts. */
    _onAnimationStart(event) {
        this._animationStateChanged.emit(event);
    }
    /** Starts the dialog exit animation. */
    _startExitAnimation() {
        this._state = 'exit';
        // Mark the container for check so it can react if the
        // view container is using OnPush change detection.
        this._changeDetectorRef.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogContainerComponent, deps: [{ token: i0.ElementRef }, { token: i1.ConfigurableFocusTrapFactory }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT, optional: true }, { token: PsDialogConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsDialogContainerComponent, selector: "ps-dialog-container", host: { listeners: { "@dialogContainer.done": "_onAnimationDone($event)", "@dialogContainer.start": "_onAnimationStart($event)" }, properties: { "@dialogContainer": "this._state", "class.ps-dialog-container": "this.psDialogContaier", "attr.tabindex": "this.tabIndex", "attr.aria-modal": "this.ariaModal", "attr.role": "this.role", "attr.aria-labelledby": "this.ariaLabelledByAttr", "attr.aria-label": "this.ariaLabel", "attr.aria-describedby": "this.ariaDescribedBy", "attr.id": "this._id", "class.ps-dialog-container-has-close-button": "this.hasCloseButton" } }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ps-icon *ngIf=\"hasCloseButton\" class=\"ps-dialog-close-icon\" (click)=\"_closeIconClicked.emit()\"\n  >close</ps-icon\n>\n<ng-template cdkPortalOutlet></ng-template>\n", styles: [".ps-dialog-container{display:block;padding:0 2.5rem 2.5rem;border-radius:1rem;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;min-height:12.5rem}.ps-dialog-container .ps-dialog-close-icon{display:flex;margin:1rem -1.5rem 0 auto;-webkit-user-select:none;user-select:none}.ps-dialog-container:not(.ps-dialog-container-has-close-button){padding:2.5rem}@media all and (min-width: 529px){.ps-dialog-container{max-height:85vh}}@media all and (max-width: 528px){.ps-dialog-container{padding:0 1rem 1rem;border-radius:0;min-width:20rem}.ps-dialog-container .ps-dialog-close-icon{margin:1rem 0 .5rem auto}.ps-dialog-container:not(.ps-dialog-container-has-close-button){padding:1rem}}@media (-ms-high-contrast: active){.ps-dialog-container{outline:solid .0625rem}}.ps-dialog-content{display:block;margin-top:1rem;overflow:auto;-webkit-overflow-scrolling:touch;position:relative;z-index:1}.ps-dialog-title{display:block;text-align:center}.ps-dialog-actions{display:flex;flex-wrap:wrap;align-items:center;position:relative;z-index:2;padding-top:2rem}.ps-dialog-content-scrollable+.ps-dialog-actions{padding-top:1rem}.ps-dialog-actions[align=end]{justify-content:flex-end}.ps-dialog-actions[align=center]{justify-content:center}.ps-dialog-actions[align=space-between]{justify-content:space-between}.ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:.5rem}[dir=rtl] .ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:0;margin-right:.5rem}@media all and (max-width: 528px){.ps-dialog-actions{padding-top:1rem;flex-direction:column-reverse}.ps-dialog-actions .ps-button-base{width:100%}.ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:0;margin-bottom:1rem}}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "component", type: i5.PsIconComponent, selector: "ps-icon", inputs: ["inline", "fontSet", "fontIcon", "color", "svgIcon", "size"], exportAs: ["psIcon"] }], animations: [psDialogAnimations.dialogContainer], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-dialog-container', encapsulation: ViewEncapsulation.None, animations: [psDialogAnimations.dialogContainer], template: "<ps-icon *ngIf=\"hasCloseButton\" class=\"ps-dialog-close-icon\" (click)=\"_closeIconClicked.emit()\"\n  >close</ps-icon\n>\n<ng-template cdkPortalOutlet></ng-template>\n", styles: [".ps-dialog-container{display:block;padding:0 2.5rem 2.5rem;border-radius:1rem;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;min-height:12.5rem}.ps-dialog-container .ps-dialog-close-icon{display:flex;margin:1rem -1.5rem 0 auto;-webkit-user-select:none;user-select:none}.ps-dialog-container:not(.ps-dialog-container-has-close-button){padding:2.5rem}@media all and (min-width: 529px){.ps-dialog-container{max-height:85vh}}@media all and (max-width: 528px){.ps-dialog-container{padding:0 1rem 1rem;border-radius:0;min-width:20rem}.ps-dialog-container .ps-dialog-close-icon{margin:1rem 0 .5rem auto}.ps-dialog-container:not(.ps-dialog-container-has-close-button){padding:1rem}}@media (-ms-high-contrast: active){.ps-dialog-container{outline:solid .0625rem}}.ps-dialog-content{display:block;margin-top:1rem;overflow:auto;-webkit-overflow-scrolling:touch;position:relative;z-index:1}.ps-dialog-title{display:block;text-align:center}.ps-dialog-actions{display:flex;flex-wrap:wrap;align-items:center;position:relative;z-index:2;padding-top:2rem}.ps-dialog-content-scrollable+.ps-dialog-actions{padding-top:1rem}.ps-dialog-actions[align=end]{justify-content:flex-end}.ps-dialog-actions[align=center]{justify-content:center}.ps-dialog-actions[align=space-between]{justify-content:space-between}.ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:.5rem}[dir=rtl] .ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:0;margin-right:.5rem}@media all and (max-width: 528px){.ps-dialog-actions{padding-top:1rem;flex-direction:column-reverse}.ps-dialog-actions .ps-button-base{width:100%}.ps-dialog-actions .ps-button-base+.ps-button-base{margin-left:0;margin-bottom:1rem}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.ConfigurableFocusTrapFactory }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: PsDialogConfig }], propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }], _state: [{
                type: HostBinding,
                args: ['@dialogContainer']
            }], psDialogContaier: [{
                type: HostBinding,
                args: ['class.ps-dialog-container']
            }], tabIndex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], ariaModal: [{
                type: HostBinding,
                args: ['attr.aria-modal']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], ariaLabelledByAttr: [{
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], ariaDescribedBy: [{
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], _id: [{
                type: HostBinding,
                args: ['attr.id']
            }], hasCloseButton: [{
                type: HostBinding,
                args: ['class.ps-dialog-container-has-close-button']
            }], _onAnimationDone: [{
                type: HostListener,
                args: ['@dialogContainer.done', ['$event']]
            }], _onAnimationStart: [{
                type: HostListener,
                args: ['@dialogContainer.start', ['$event']]
            }] } });

// Counter for unique dialog ids.
let uniqueId = 0;
/**
 * Dialog design dimensions that should be passed to the respective
 * position strategy of the overlay service in order to propertly
 * size the dialog.
 */
const DIALOG_DIMENSIONS = {
    webWidth: '496px',
    webTopOffset: '100px',
    mobileWidth: '100vw',
    mobileHeight: '100vh',
    mobileTopOffset: '0px',
    mobileBreakpoint: 528
};
/**
 * Reference to a dialog opened via the PsDialog service.
 */
class PsDialogRef {
    constructor(_overlayRef, _containerInstance, 
    // @breaking-change 8.0.0 `_location` parameter to be removed.
    _location, id = `ps-dialog-${uniqueId++}`) {
        this._overlayRef = _overlayRef;
        this._containerInstance = _containerInstance;
        this.id = id;
        /** Whether the user is allowed to close the dialog. */
        this.disableClose = this._containerInstance._config.disableClose;
        /** Subject for notifying the user that the dialog has finished opening. */
        this._afterOpened = new Subject();
        /** Subject for notifying the user that the dialog has finished closing. */
        this._afterClosed = new Subject();
        /** Subject for notifying the user that the dialog has started closing. */
        this._beforeClosed = new Subject();
        // Pass the id along to the container.
        _containerInstance._id = id;
        // Emit when opening animation completes
        _containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && event.toState === 'enter'), take(1))
            .subscribe(() => {
            this._afterOpened.next();
            this._afterOpened.complete();
        });
        // Dispose overlay when closing animation is complete
        _containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && event.toState === 'exit'), take(1))
            .subscribe(() => {
            clearTimeout(this._closeFallbackTimeout);
            this._overlayRef.dispose();
        });
        _containerInstance._closeIconClicked
            .pipe(takeUntil(this._afterClosed))
            .subscribe(() => this.close());
        _overlayRef.detachments().subscribe(() => {
            this._beforeClosed.next(this._result);
            this._beforeClosed.complete();
            this._afterClosed.next(this._result);
            this._afterClosed.complete();
            this.componentInstance = null;
            this._overlayRef.dispose();
        });
        _overlayRef
            .keydownEvents()
            .pipe(filter((event) => 
        // eslint-disable-next-line import/no-deprecated
        event.keyCode === ESCAPE && !this.disableClose && !hasModifierKey(event)))
            .subscribe((event) => {
            event.preventDefault();
            this.close();
        });
        /**
         * The 'ps-dialog-content' listens for the window's size event and it invokes the update
         * method. However, if the 'ps-dialog-content' is not present, we should manually hook for
         * the event in order to invoke the update method.
         */
        if (!this._containerInstance.contentElement) {
            fromEvent(window, 'resize')
                .pipe(debounceTime(50), distinctUntilChanged(), takeUntil(this._afterClosed))
                .subscribe(() => {
                this.updateResponsiveSizeAndPosition();
            });
        }
    }
    /**
     * Close the dialog.
     * @param dialogResult Optional result to return to the dialog opener.
     */
    close(dialogResult) {
        this._result = dialogResult;
        // Transition the backdrop in parallel to the dialog.
        this._containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'start'), take(1))
            .subscribe((event) => {
            this._beforeClosed.next(dialogResult);
            this._beforeClosed.complete();
            this._overlayRef.detachBackdrop();
            // The logic that disposes of the overlay depends on the exit animation completing,however
            // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
            // timeout which will clean everything up if the animation hasn't fired within the
            // specified amount of time plus 100ms. We don't need to run this outside the NgZone,
            // because for the vast majority of cases the timeout will have been cleared before
            // it has the chance to fire.
            this._closeFallbackTimeout = window.setTimeout(() => {
                this._overlayRef.dispose();
            }, event.totalTime + 100);
        });
        this._containerInstance._startExitAnimation();
    }
    /**
     * Gets an observable that is notified when the dialog is finished opening.
     */
    afterOpened() {
        return this._afterOpened.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog is finished closing.
     */
    afterClosed() {
        return this._afterClosed.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog has started closing.
     */
    beforeClosed() {
        return this._beforeClosed.asObservable();
    }
    /**
     * Gets an observable that emits when the overlay's backdrop has been clicked.
     */
    backdropClick() {
        return this._overlayRef.backdropClick();
    }
    /**
     * Gets an observable that emits when keydown events are targeted on the overlay.
     */
    keydownEvents() {
        return this._overlayRef.keydownEvents();
    }
    /**
     * Updates the dialog's position.
     * @param position New dialog position.
     */
    updatePosition(position) {
        const strategy = this._getPositionStrategy();
        if (position && (position.left || position.right)) {
            if (position.left) {
                strategy.left(position.left);
            }
            else {
                strategy.right(position.right);
            }
        }
        else {
            strategy.centerHorizontally();
        }
        if (position && (position.top || position.bottom)) {
            if (position.top) {
                strategy.top(position.top);
            }
            else {
                strategy.bottom(position.bottom);
            }
        }
        else {
            /**
             * We position the dialog at the top of the page by default
             * in order to have room for the show/hide animations, which
             * move the dialog to bottom and top respectively with specific amount.
             */
            strategy.top('0px');
        }
        this._overlayRef.updatePosition();
        return this;
    }
    /**
     * Updates the dialog's width and height.
     * @param width New width of the dialog.
     * @param height New height of the dialog.
     */
    updateSize(width = '', height = '') {
        /**
         * We still set the width and height through the position strategy,
         * because there is an issue in the Angular CDK for the overlay, where
         * the overlay ignores the width and height if they are set through
         * the overlayConfig. Until we update the Angular CDK version,
         * we should use this approach and disable the deprecation warning.
         * See https://github.com/angular/components/issues/9715
         */
        this._getPositionStrategy()
            // eslint-disable-next-line import/no-deprecated
            .width(width)
            // eslint-disable-next-line import/no-deprecated
            .height(height);
        this._overlayRef.updatePosition();
        return this;
    }
    /** Add a CSS class or an array of classes to the overlay pane. */
    addPanelClass(classes) {
        this._overlayRef.addPanelClass(classes);
        return this;
    }
    /** Remove a CSS class or an array of classes from the overlay pane. */
    removePanelClass(classes) {
        this._overlayRef.removePanelClass(classes);
        return this;
    }
    /**
     * Gets an observable that is notified when the dialog is finished opening.
     * @deprecated Use `afterOpened` instead.
     * @breaking-change 8.0.0
     */
    afterOpen() {
        return this.afterOpened();
    }
    /**
     * Gets an observable that is notified when the dialog has started closing.
     * @deprecated Use `beforeClosed` instead.
     * @breaking-change 8.0.0
     */
    beforeClose() {
        return this.beforeClosed();
    }
    /** Fetches the position strategy object from the overlay ref. */
    _getPositionStrategy() {
        return this._overlayRef.getConfig().positionStrategy;
    }
    get isMobileView() {
        const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth ? window.innerWidth : 0);
        return windowWidth <= DIALOG_DIMENSIONS.mobileBreakpoint;
    }
    updateResponsiveSizeAndPosition() {
        const widthValue = this.isMobileView
            ? DIALOG_DIMENSIONS.mobileWidth
            : DIALOG_DIMENSIONS.webWidth;
        // No height should be set when in web mode.
        const heightValue = this.isMobileView ? DIALOG_DIMENSIONS.mobileHeight : '';
        const positionValue = {
            top: this.isMobileView ? DIALOG_DIMENSIONS.mobileTopOffset : DIALOG_DIMENSIONS.webTopOffset
        };
        this.updateSize(widthValue, heightValue).updatePosition(positionValue);
    }
}

/** Injection token that can be used to access the data that was passed in to a dialog. */
const PS_DIALOG_DATA = new InjectionToken('PsDialogData');
/** Injection token that can be used to specify default dialog options. */
const PS_DIALOG_DEFAULT_OPTIONS = new InjectionToken('ps-dialog-default-options');
/** Injection token that determines the scroll handling while the dialog is open. */
const PS_DIALOG_SCROLL_STRATEGY = new InjectionToken('ps-dialog-scroll-strategy');
/** @docs-private */
const PS_DIALOG_SCROLL_STRATEGY_FACTORY = (overlay) => () => overlay.scrollStrategies.block();
/** @docs-private */
const PS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY = (overlay) => () => overlay.scrollStrategies.block();
/** @docs-private */
const PS_DIALOG_SCROLL_STRATEGY_PROVIDER = {
    provide: PS_DIALOG_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: PS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY
};
/**
 * Applies default options to the dialog config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
const _applyConfigDefaults = (config, defaultOptions) => ({ ...defaultOptions, ...config });
/**
 * Service to open modal dialogs.
 */
class PsDialogService {
    /** Keeps track of the currently-open dialogs. */
    get openDialogs() {
        return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
    }
    /** Stream that emits when a dialog has been opened. */
    get afterOpened() {
        return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
    }
    /**
     * Stream that emits when a dialog has been opened.
     * @deprecated Use `afterOpened` instead.
     * @breaking-change 8.0.0
     */
    get afterOpen() {
        return this.afterOpened;
    }
    get _afterAllClosed() {
        const parent = this._parentDialog;
        return parent ? parent._afterAllClosed : this._afterAllClosedAtThisLevel;
    }
    constructor(_overlay, _injector, _location, _defaultOptions, scrollStrategy, _parentDialog, _overlayContainer) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._location = _location;
        this._defaultOptions = _defaultOptions;
        this._parentDialog = _parentDialog;
        this._overlayContainer = _overlayContainer;
        this._openDialogsAtThisLevel = [];
        this._afterAllClosedAtThisLevel = new Subject();
        this._afterOpenedAtThisLevel = new Subject();
        this._ariaHiddenElements = new Map();
        this.ariaHidden = 'aria-hidden';
        // TODO (): tighten the typing right-hand side of this expression.
        /**
         * Stream that emits when all open dialog have finished closing.
         * Will emit on subscribe if there are no open dialogs to begin with.
         */
        this.afterAllClosed = defer(() => {
            const undefinedValue = undefined;
            return this.openDialogs.length
                ? this._afterAllClosed
                : this._afterAllClosed.pipe(startWith(undefinedValue));
        });
        this._scrollStrategy = scrollStrategy;
    }
    /**
     * Opens a modal dialog containing the given component.
     * @param componentOrTemplateRef Type of the component to load into the dialog,
     *     or a TemplateRef to instantiate as the dialog content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened dialog.
     */
    open(componentOrTemplateRef, config) {
        config = _applyConfigDefaults(config, this._defaultOptions || new PsDialogConfig());
        if (config.id && this.getDialogById(config.id)) {
            throw Error(`Dialog with id "${config.id}" exists already. The dialog id must be unique.`);
        }
        const overlayRef = this._createOverlay(config);
        const dialogContainer = this._attachDialogContainer(overlayRef, config);
        const dialogRef = this._attachDialogContent(componentOrTemplateRef, dialogContainer, overlayRef, config);
        // If this is the first dialog that we're opening, hide all the non-overlay content.
        if (!this.openDialogs.length) {
            this._hideNonDialogContentFromAssistiveTechnology();
        }
        this.openDialogs.push(dialogRef);
        dialogRef.afterClosed().subscribe(() => this._removeOpenDialog(dialogRef));
        this.afterOpened.next(dialogRef);
        return dialogRef;
    }
    /**
     * Closes all of the currently-open dialogs.
     */
    closeAll() {
        this._closeDialogs(this.openDialogs);
    }
    /**
     * Finds an open dialog by its id.
     * @param id ID to use when looking up the dialog.
     */
    getDialogById(id) {
        return this.openDialogs.find((dialog) => dialog.id === id);
    }
    ngOnDestroy() {
        // Only close the dialogs at this level on destroy
        // since the parent service may still be active.
        this._closeDialogs(this._openDialogsAtThisLevel);
        this._afterAllClosedAtThisLevel.complete();
        this._afterOpenedAtThisLevel.complete();
    }
    /**
     * Creates the overlay into which the dialog will be loaded.
     * @param config The dialog configuration.
     * @returns A promise resolving to the OverlayRef for the created overlay.
     */
    _createOverlay(config) {
        const overlayConfig = this._getOverlayConfig(config);
        return this._overlay.create(overlayConfig);
    }
    /**
     * Creates an overlay config from a dialog config.
     * @param dialogConfig The dialog configuration.
     * @returns The overlay configuration.
     */
    _getOverlayConfig(dialogConfig) {
        const state = new OverlayConfig({
            positionStrategy: this._overlay.position().global(),
            scrollStrategy: dialogConfig.scrollStrategy || this._scrollStrategy(),
            panelClass: dialogConfig.panelClass,
            hasBackdrop: dialogConfig.hasBackdrop,
            direction: dialogConfig.direction,
            disposeOnNavigation: dialogConfig.closeOnNavigation
        });
        if (dialogConfig.backdropClass) {
            state.backdropClass = dialogConfig.backdropClass;
        }
        return state;
    }
    /**
     * Attaches an PsDialogContainer to a dialog's already-created overlay.
     * @param overlay Reference to the dialog's underlying overlay.
     * @param config The dialog configuration.
     * @returns A promise resolving to a ComponentRef for the attached container.
     */
    _attachDialogContainer(overlay, config) {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = new PortalInjector(userInjector || this._injector, new WeakMap([[PsDialogConfig, config]]));
        const containerPortal = new ComponentPortal(PsDialogContainerComponent, config.viewContainerRef, injector);
        const containerRef = overlay.attach(containerPortal);
        return containerRef.instance;
    }
    /**
     * Attaches the user-provided component to the already-created PsDialogContainer.
     * @param componentOrTemplateRef The type of component being loaded into the dialog,
     *     or a TemplateRef to instantiate as the content.
     * @param dialogContainer Reference to the wrapping PsDialogContainer.
     * @param overlayRef Reference to the overlay in which the dialog resides.
     * @param config The dialog configuration.
     * @returns A promise resolving to the PsDialogRef that should be returned to the user.
     */
    _attachDialogContent(componentOrTemplateRef, dialogContainer, overlayRef, config) {
        // Create a reference to the dialog we're creating in order to give the user a handle
        // to modify and close it.
        const dialogRef = new PsDialogRef(overlayRef, dialogContainer, this._location, config.id);
        // When the dialog backdrop is clicked, we want to close it.
        if (config.hasBackdrop) {
            overlayRef.backdropClick().subscribe(() => {
                if (!dialogRef.disableClose) {
                    dialogRef.close();
                }
            });
        }
        if (componentOrTemplateRef instanceof TemplateRef) {
            dialogContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplateRef, null, {
                $implicit: config.data,
                dialogRef
            }));
        }
        else {
            const injector = this._createInjector(config, dialogRef, dialogContainer);
            const contentRef = dialogContainer.attachComponentPortal(new ComponentPortal(componentOrTemplateRef, undefined, injector));
            dialogRef.componentInstance = contentRef.instance;
        }
        dialogRef.updateResponsiveSizeAndPosition();
        return dialogRef;
    }
    /**
     * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
     * of a dialog to close itself and, optionally, to return a value.
     * @param config Config object that is used to construct the dialog.
     * @param dialogRef Reference to the dialog.
     * @param container Dialog container element that wraps all of the contents.
     * @returns The custom injector that can be used inside the dialog.
     */
    _createInjector(config, dialogRef, dialogContainer) {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        // The PsDialogContainer is injected in the portal as the PsDialogContainer and the dialog's
        // content are created out of the same ViewContainerRef and as such, are siblings for injector
        // purposes. To allow the hierarchy that is expected, the PsDialogContainer is explicitly
        // added to the injection tokens.
        const injectionTokens = new WeakMap([
            [PsDialogContainerComponent, dialogContainer],
            [PS_DIALOG_DATA, config.data],
            [PsDialogRef, dialogRef]
        ]);
        if (config.direction &&
            (!userInjector || !userInjector.get(Directionality, null))) {
            injectionTokens.set(Directionality, {
                value: config.direction,
                change: of()
            });
        }
        return new PortalInjector(userInjector || this._injector, injectionTokens);
    }
    /**
     * Removes a dialog from the array of open dialogs.
     * @param dialogRef Dialog to be removed.
     */
    _removeOpenDialog(dialogRef) {
        const index = this.openDialogs.indexOf(dialogRef);
        if (index > -1) {
            this.openDialogs.splice(index, 1);
            // If all the dialogs were closed, remove/restore the `aria-hidden`
            // to a the siblings and emit to the `afterAllClosed` stream.
            if (!this.openDialogs.length) {
                this._ariaHiddenElements.forEach((previousValue, element) => {
                    if (previousValue) {
                        element.setAttribute(this.ariaHidden, previousValue);
                    }
                    else {
                        element.removeAttribute(this.ariaHidden);
                    }
                });
                this._ariaHiddenElements.clear();
                this._afterAllClosed.next();
            }
        }
    }
    /**
     * Hides all of the content that isn't an overlay from assistive technology.
     */
    _hideNonDialogContentFromAssistiveTechnology() {
        const overlayContainer = this._overlayContainer.getContainerElement();
        // Ensure that the overlay container is attached to the DOM.
        if (overlayContainer.parentElement) {
            const siblings = overlayContainer.parentElement.children;
            for (let i = siblings.length - 1; i > -1; i--) {
                const sibling = siblings[i];
                if (sibling !== overlayContainer &&
                    sibling.nodeName !== 'SCRIPT' &&
                    sibling.nodeName !== 'STYLE' &&
                    !sibling.hasAttribute('aria-live')) {
                    this._ariaHiddenElements.set(sibling, sibling.getAttribute(this.ariaHidden));
                    sibling.setAttribute(this.ariaHidden, 'true');
                }
            }
        }
    }
    /** Closes all of the dialogs in an array. */
    // eslint-disable-next-line class-methods-use-this
    _closeDialogs(dialogs) {
        let i = dialogs.length;
        while (i--) {
            // The `_openDialogs` property isn't updated after close until the rxjs subscription
            // runs on the next microtask, in addition to modifying the array as we're going
            // through it. We loop through all of them and call close without assuming that
            // they'll be removed from the list instantaneously.
            dialogs[i].close();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogService, deps: [{ token: i1$1.Overlay }, { token: i0.Injector }, { token: i2.Location, optional: true }, { token: PS_DIALOG_DEFAULT_OPTIONS, optional: true }, { token: PS_DIALOG_SCROLL_STRATEGY }, { token: PsDialogService, optional: true, skipSelf: true }, { token: i1$1.OverlayContainer }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.Overlay }, { type: i0.Injector }, { type: i2.Location, decorators: [{
                    type: Optional
                }] }, { type: PsDialogConfig, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PS_DIALOG_DEFAULT_OPTIONS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PS_DIALOG_SCROLL_STRATEGY]
                }] }, { type: PsDialogService, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: i1$1.OverlayContainer }] });

const DIALOG_ACTIONS_SHADOW_CLASS = 'ps-dialog-actions-shadow';
/**
 * Finds the closest PsDialogRef to an element by looking at the DOM.
 * @param element Element relative to which to look for a dialog.
 * @param openDialogs References to the currently-open dialogs.
 */
const getClosestDialog = (element, openDialogs) => {
    let parent = element.nativeElement.parentElement;
    while (parent && !parent.classList.contains('ps-dialog-container')) {
        parent = parent.parentElement;
    }
    return parent ? openDialogs.find((dialog) => dialog.id === parent.id) : null;
};
/** Counter used to generate unique IDs for dialog elements. */
let dialogElementUid = 0;
/**
 * Button that will close the current dialog.
 */
class PsDialogCloseDirective {
    get psDialogClose() {
        return true;
    }
    constructor(dialogRef, _elementRef, _dialog) {
        this.dialogRef = dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
        this.type = 'button';
    }
    ngOnInit() {
        if (!this.dialogRef) {
            // When this directive is included in a dialog via TemplateRef (rather than being
            // in a Component), the DialogRef isn't available via injection because embedded
            // views cannot be given a custom injector. Instead, we look up the DialogRef by
            // ID. This must occur in `onInit`, as the ID binding for the dialog container won't
            // be resolved at constructor time.
            const closestDialog = getClosestDialog(this._elementRef, this._dialog.openDialogs);
            this.dialogRef = closestDialog || this.dialogRef;
        }
    }
    ngOnChanges(changes) {
        const proxiedChange = changes._psDialogClose || changes._psDialogCloseResult;
        if (proxiedChange) {
            this.dialogResult = proxiedChange.currentValue;
        }
    }
    onClick() {
        this.dialogRef.close(this.dialogResult);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogCloseDirective, deps: [{ token: PsDialogRef, optional: true }, { token: i0.ElementRef }, { token: PsDialogService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsDialogCloseDirective, selector: "[ps-dialog-close], [psDialogClose]", inputs: { ariaLabel: ["aria-label", "ariaLabel"], dialogResult: ["ps-dialog-close", "dialogResult"], _psDialogClose: ["psDialogClose", "_psDialogClose"] }, host: { listeners: { "click": "onClick()" }, properties: { "attr.aria-label": "this.ariaLabel", "attr.type": "this.type", "class.ps-dialog-close": "this.psDialogClose" } }, exportAs: ["psDialogClose"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-dialog-close], [psDialogClose]',
                    exportAs: 'psDialogClose'
                }]
        }], ctorParameters: () => [{ type: PsDialogRef, decorators: [{
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: PsDialogService }], propDecorators: { ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }, {
                type: Input,
                args: ['aria-label']
            }], dialogResult: [{
                type: Input,
                args: ['ps-dialog-close']
            }], _psDialogClose: [{
                type: Input,
                args: ['psDialogClose']
            }], type: [{
                type: HostBinding,
                args: ['attr.type']
            }], psDialogClose: [{
                type: HostBinding,
                args: ['class.ps-dialog-close']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });
/**
 * Title of a dialog element. Stays fixed to the top of the dialog when scrolling.
 */
class PsDialogTitleDirective {
    get psDialogTitle() {
        return true;
    }
    constructor(_dialogRef, _elementRef, _dialog) {
        this._dialogRef = _dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
        this.id = `ps-dialog-title-${dialogElementUid++}`;
    }
    ngOnInit() {
        if (!this._dialogRef) {
            const closestDialog = getClosestDialog(this._elementRef, this._dialog.openDialogs);
            this._dialogRef = closestDialog || this._dialogRef;
        }
        if (this._dialogRef) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            Promise.resolve().then(() => {
                const container = this._dialogRef._containerInstance;
                if (container && !container._ariaLabelledBy) {
                    container._ariaLabelledBy = this.id;
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogTitleDirective, deps: [{ token: PsDialogRef, optional: true }, { token: i0.ElementRef }, { token: PsDialogService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsDialogTitleDirective, selector: "[ps-dialog-title], [psDialogTitle]", inputs: { id: "id" }, host: { properties: { "id": "this.id", "attr.id": "this.id", "class.ps-dialog-title": "this.psDialogTitle" } }, exportAs: ["psDialogTitle"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-dialog-title], [psDialogTitle]',
                    exportAs: 'psDialogTitle'
                }]
        }], ctorParameters: () => [{ type: PsDialogRef, decorators: [{
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: PsDialogService }], propDecorators: { id: [{
                type: HostBinding,
                args: ['id']
            }, {
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }], psDialogTitle: [{
                type: HostBinding,
                args: ['class.ps-dialog-title']
            }] } });
/**
 * Scrollable content container of a dialog.
 */
class PsDialogContentDirective {
    get maxHeight() {
        return this._maxHeight;
    }
    get scrollable() {
        return this._scrollable;
    }
    get psDialogContent() {
        return true;
    }
    get nativeElement() {
        return this._elementRef.nativeElement;
    }
    constructor(_dialogRef, _elementRef, _dialog, _renderer, _cdr) {
        this._dialogRef = _dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
        this._renderer = _renderer;
        this._cdr = _cdr;
        this._destroyed = new Subject();
        fromEvent(window, 'resize')
            .pipe(debounceTime(50), distinctUntilChanged(), takeUntil(this._destroyed))
            .subscribe(() => {
            this.updateMaxHeight();
            this.updateScrollShadow();
            this._dialogRef.updateResponsiveSizeAndPosition();
        });
    }
    ngOnInit() {
        if (!this._dialogRef) {
            const closestDialog = getClosestDialog(this._elementRef, this._dialog.openDialogs);
            this._dialogRef = closestDialog || this._dialogRef;
            this._cdr.detectChanges();
        }
    }
    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.updateMaxHeight();
            /**
             * In order to determine if the content is scrollable, we need to have updated the
             * maxHeight first. After that, if the content is indeed scrollable, we would need to
             * recalculate it in order to get the respective actionsArea padding.
             */
            this.updateScrollable();
            if (this._scrollable) {
                this.updateMaxHeight();
            }
            this.updateScrollShadow();
        });
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    onScroll() {
        this.updateScrollShadow();
    }
    updateScrollShadow() {
        if (!this._dialogRef._containerInstance.actionsElement) {
            return;
        }
        const remainingScrollHeight = this.nativeElement.scrollHeight - this.nativeElement.scrollTop;
        const scrolledToBottom = remainingScrollHeight === this.nativeElement.clientHeight;
        if (!scrolledToBottom) {
            this._renderer.addClass(this._dialogRef._containerInstance.actionsElement, DIALOG_ACTIONS_SHADOW_CLASS);
        }
        else {
            this._renderer.removeClass(this._dialogRef._containerInstance.actionsElement, DIALOG_ACTIONS_SHADOW_CLASS);
        }
        this._cdr.detectChanges();
    }
    updateMaxHeight() {
        if (!this._dialogRef || !this._dialogRef._containerInstance) {
            return;
        }
        const dialogContainerNativeElement = this._dialogRef._containerInstance.nativeElement;
        const maxHeight = this._dialogRef.isMobileView
            ? dialogContainerNativeElement.getBoundingClientRect().height
            : parseInt(getComputedStyle(dialogContainerNativeElement).maxHeight, 10);
        // Compensate for bottom padding and height of the entire dialog container.
        const bottomPadding = parseInt(getComputedStyle(dialogContainerNativeElement).paddingBottom, 10);
        // Compensate for close icon on top (if it exists) and its margins.
        // If the close icon does not exist, we will use the top padding instead.
        const closeIcon = this._dialogRef._containerInstance.closeIconElement;
        const topOffset = closeIcon
            ? closeIcon.getBoundingClientRect().height +
                parseInt(getComputedStyle(closeIcon).marginTop, 10) +
                parseInt(getComputedStyle(closeIcon).marginBottom, 10)
            : parseInt(getComputedStyle(dialogContainerNativeElement).paddingTop, 10);
        // Compensate for title's height
        const title = this._dialogRef._containerInstance.titleElement;
        const titleHeight = title ? title.getBoundingClientRect().height : 0;
        // Compensate for content's top margin.
        const contentAreaMarginTop = parseInt(getComputedStyle(this._elementRef.nativeElement).marginTop, 10);
        // Compensate for actions' height and margin.
        const actionsArea = this._dialogRef._containerInstance.actionsElement;
        const actionsAreaHeight = actionsArea ? actionsArea.getBoundingClientRect().height : 0;
        // The resulting value is the available space for the content area.
        const result = `${maxHeight - topOffset - bottomPadding - titleHeight - actionsAreaHeight - contentAreaMarginTop}`;
        if (this._maxHeight !== result) {
            this._maxHeight = result;
            this._cdr.detectChanges();
        }
    }
    updateScrollable() {
        this._scrollable = this.nativeElement.scrollHeight > this.nativeElement.clientHeight;
        this._cdr.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogContentDirective, deps: [{ token: PsDialogRef, optional: true }, { token: i0.ElementRef }, { token: PsDialogService }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsDialogContentDirective, selector: "[ps-dialog-content], ps-dialog-content, [psDialogContent]", host: { listeners: { "scroll": "onScroll()" }, properties: { "style.max-height.px": "this.maxHeight", "class.ps-dialog-content-scrollable": "this.scrollable", "class.ps-dialog-content": "this.psDialogContent" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-dialog-content], ps-dialog-content, [psDialogContent]'
                }]
        }], ctorParameters: () => [{ type: PsDialogRef, decorators: [{
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: PsDialogService }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }], propDecorators: { maxHeight: [{
                type: HostBinding,
                args: ['style.max-height.px']
            }], scrollable: [{
                type: HostBinding,
                args: ['class.ps-dialog-content-scrollable']
            }], psDialogContent: [{
                type: HostBinding,
                args: ['class.ps-dialog-content']
            }], onScroll: [{
                type: HostListener,
                args: ['scroll']
            }] } });
/**
 * Container for the bottom action buttons in a dialog.
 * Stays fixed to the bottom when scrolling.
 */
class PsDialogActionsDirective {
    get psDialogActions() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogActionsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsDialogActionsDirective, selector: "[ps-dialog-actions], ps-dialog-actions, [psDialogActions]", host: { properties: { "class.ps-dialog-actions": "this.psDialogActions" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogActionsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-dialog-actions], ps-dialog-actions, [psDialogActions]'
                }]
        }], propDecorators: { psDialogActions: [{
                type: HostBinding,
                args: ['class.ps-dialog-actions']
            }] } });
/**
 * Directive that will pass data by clicking on the close icon button (X).
 */
class PsCloseIconDataDirective {
    set closeIconData(closeIconData) {
        this.sendResultOnDialogClose(closeIconData);
    }
    set psCloseIconData(closeIconData) {
        this.sendResultOnDialogClose(closeIconData);
    }
    constructor(dialogRef, _elementRef, _dialog) {
        this.dialogRef = dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
        this.destroyed$ = new Subject();
    }
    ngOnInit() {
        if (!this.dialogRef) {
            const closestDialog = getClosestDialog(this._elementRef, this._dialog.openDialogs);
            this.dialogRef = closestDialog || this.dialogRef;
        }
    }
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
    sendResultOnDialogClose(closeIconData) {
        if (!closeIconData) {
            return;
        }
        this.dialogRef._containerInstance._closeIconClicked
            .asObservable()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.dialogRef.close(closeIconData));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCloseIconDataDirective, deps: [{ token: PsDialogRef, optional: true }, { token: i0.ElementRef }, { token: PsDialogService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCloseIconDataDirective, selector: "[ps-close-icon-data], [psCloseIconData]", inputs: { closeIconData: ["ps-close-icon-data", "closeIconData"], psCloseIconData: "psCloseIconData" }, exportAs: ["psCloseIconData"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCloseIconDataDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[ps-close-icon-data], [psCloseIconData]', exportAs: 'psCloseIconData' }]
        }], ctorParameters: () => [{ type: PsDialogRef, decorators: [{
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: PsDialogService }], propDecorators: { closeIconData: [{
                type: Input,
                args: ['ps-close-icon-data']
            }], psCloseIconData: [{
                type: Input,
                args: ['psCloseIconData']
            }] } });

class PsDialogModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsDialogModule, declarations: [PsDialogContainerComponent,
            PsDialogCloseDirective,
            PsDialogTitleDirective,
            PsDialogActionsDirective,
            PsDialogContentDirective,
            PsCloseIconDataDirective], imports: [CommonModule, OverlayModule, PortalModule, PsCommonModule, PsIconModule], exports: [PsDialogContainerComponent,
            PsDialogCloseDirective,
            PsDialogTitleDirective,
            PsDialogContentDirective,
            PsDialogActionsDirective,
            PsCloseIconDataDirective,
            PsCommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogModule, providers: [PsDialogService, PS_DIALOG_SCROLL_STRATEGY_PROVIDER], imports: [CommonModule, OverlayModule, PortalModule, PsCommonModule, PsIconModule, PsCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule, PortalModule, PsCommonModule, PsIconModule],
                    exports: [
                        PsDialogContainerComponent,
                        PsDialogCloseDirective,
                        PsDialogTitleDirective,
                        PsDialogContentDirective,
                        PsDialogActionsDirective,
                        PsCloseIconDataDirective,
                        PsCommonModule
                    ],
                    declarations: [
                        PsDialogContainerComponent,
                        PsDialogCloseDirective,
                        PsDialogTitleDirective,
                        PsDialogActionsDirective,
                        PsDialogContentDirective,
                        PsCloseIconDataDirective
                    ],
                    providers: [PsDialogService, PS_DIALOG_SCROLL_STRATEGY_PROVIDER]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PS_DIALOG_DATA, PS_DIALOG_DEFAULT_OPTIONS, PS_DIALOG_SCROLL_STRATEGY, PS_DIALOG_SCROLL_STRATEGY_FACTORY, PS_DIALOG_SCROLL_STRATEGY_PROVIDER, PS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY, PsCloseIconDataDirective, PsDialogActionsDirective, PsDialogCloseDirective, PsDialogConfig, PsDialogContainerComponent, PsDialogContentDirective, PsDialogModule, PsDialogRef, PsDialogService, PsDialogTitleDirective, psDialogAnimations, throwPsDialogContentAlreadyAttachedError };
